import clsx from 'clsx';
import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { SITE_PROXY_ORIGIN_PATTERN, type SiteProxy } from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Actions,
    Alert,
    alertKindPropsMap,
    Button,
    clipboard,
    confirm,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    FieldError,
    Form,
    Group,
    Heading,
    Link,
    RevealableToolbar,
    RevealableToolbarItem,
    RevealableToolbarRoot,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    DocumentationLink,
    getPaidIndicatorBadgeProps,
    PaidFeatureIndicator,
} from '../../../../../Components';
import { apiClient, useSiteContext, useUpgradeSite } from '../../../../../state';

export const SetupCustomProxy = React.memo(function SetupCustomProxy() {
    const customProxyDialogTrigger = useDialogTriggerState();
    const { site } = useSiteContext();

    return (
        <>
            {site.proxy ? (
                <ConfiguredCustomProxy
                    proxy={site.proxy}
                    onSetupProxy={customProxyDialogTrigger.open}
                />
            ) : (
                <SetupSiteCustomProxy onSetupProxy={customProxyDialogTrigger.open} />
            )}

            <DialogRoot {...customProxyDialogTrigger.rootProps}>
                <SiteCustomProxyDialog />
            </DialogRoot>
        </>
    );
});

function ConfiguredCustomProxy(props: { proxy: SiteProxy; onSetupProxy: () => void }) {
    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomSubdirectory);
    const alertProps = alertKindPropsMap.success;

    return (
        <div className="flex flex-col">
            <ActionableContainer orientation="horizontal">
                <Group>
                    <Group>
                        <alertProps.icon
                            className={clsx('m-nudge-icon size-4', alertProps.iconColor)}
                        />
                        <Heading>Subdirectory</Heading>
                        <PaidFeatureIndicator upgrade={upgrade} isFeatureActive={!!site.proxy} />
                    </Group>
                    <Description>
                        Your site will be served from the subdirectory at{' '}
                        <strong>
                            <Link target="_blank" href={`https://${props.proxy.origin}`}>
                                {props.proxy.origin}
                            </Link>
                        </strong>
                    </Description>
                </Group>
                <Actions>
                    <CustomProxyPaletteOptionsButton
                        onConfigure={props.onSetupProxy}
                        proxy={props.proxy}
                    />
                </Actions>
            </ActionableContainer>
            {site.proxy ? (
                <Alert
                    size="small"
                    kind="info"
                    className="mt-4"
                    title="Additional configuration"
                    details={
                        <p>
                            This docs site is set up to be served from{' '}
                            <strong>{props.proxy.origin}</strong>. You will need to configure the
                            domain settings with your provider to ensure requests from this
                            subdirectory are properly proxied to{' '}
                            <div className="inline-flex">
                                <RevealableToolbarRoot>
                                    <strong>{props.proxy.target}</strong>
                                    <RevealableToolbar aria-label="Actions" when="hover">
                                        <RevealableToolbarItem>
                                            <Button
                                                kind="blank"
                                                size="small"
                                                label="Copy URL"
                                                leadingIcon={Icon.Copy}
                                                className="m-nudge-icon"
                                                onPress={() => {
                                                    clipboard.writeText(props.proxy.target);
                                                }}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                }}
                                                iconOnly
                                            />
                                        </RevealableToolbarItem>
                                    </RevealableToolbar>
                                </RevealableToolbarRoot>
                            </div>
                            <br />
                            <DocumentationLink
                                className="text-ui-medium"
                                path="publishing-documentation/setting-a-custom-subdirectory"
                            >
                                Learn more
                            </DocumentationLink>
                        </p>
                    }
                />
            ) : null}
        </div>
    );
}

function SetupSiteCustomProxy(props: { onSetupProxy: () => void }) {
    const { site } = useSiteContext();
    const hasCustomHostname = Boolean(site.hostname);
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomSubdirectory);

    return (
        <ActionableContainer orientation="horizontal">
            <Group>
                <Group>
                    <Heading>Subdirectory</Heading>
                    <PaidFeatureIndicator upgrade={upgrade} />
                </Group>
                <Description>
                    Host your site at a specific path on your existing domain (e.g.
                    yourcompany.com/docs).
                </Description>
            </Group>

            <Button
                disabled={hasCustomHostname}
                title={
                    hasCustomHostname
                        ? 'This site is already using a custom domain. Please remove it before setting up a subdirectory.'
                        : undefined
                }
                kind="secondary"
                size="medium"
                label="Set up a subdirectory"
                leadingIcon={Icon.Subdirectory}
                onPress={() => {
                    if (upgrade) {
                        return upgrade.onPress();
                    }
                    props.onSetupProxy();
                }}
                className="shrink-0"
            />
        </ActionableContainer>
    );
}

function CustomProxyPaletteOptionsButton(props: { onConfigure: () => void; proxy: SiteProxy }) {
    const { proxy, onConfigure } = props;
    const { site, organizationId } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomSubdirectory);

    const removeProxy = async () => {
        void confirm({
            title: 'Remove subdirectory?',
            description: `Content will no longer be served under the subdirectory at ${proxy}. You can configure it again later.`,
            kind: 'danger',
            confirmLabel: 'Remove',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        await apiClient.sites.updateSiteById(organizationId, site.id, {
                            proxy: null,
                        });
                        toast.success('Subdirectory removed');
                    },
                    'Error while removing subdirectory',
                    { onReject: preventClose }
                );
            },
        });
    };

    return (
        <PaletteButton
            kind="secondary"
            label="Manage subdirectory"
            leadingIcon={Icon.Subdirectory}
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'commands',
                            commands: [
                                {
                                    key: 'config',
                                    title: 'Configure subdirectory',
                                    icon: Icon.Settings,
                                    trigger: upgrade ? upgrade.onPress : onConfigure,
                                    badge: getPaidIndicatorBadgeProps({
                                        upgrade,
                                        isFeatureActive: !!proxy,
                                    }),
                                },
                                {
                                    key: 'remove',
                                    title: 'Remove subdirectory',
                                    icon: Icon.X,
                                    trigger: removeProxy,
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}

interface CustomProxyFormInputs {
    proxy: string;
}

const SiteProxyRegex = new RegExp(SITE_PROXY_ORIGIN_PATTERN);

function validateSiteCustomProxy(value: string) {
    if (SiteProxyRegex.test(value)) {
        return true;
    }
    return 'Must be a valid subdirectory path (e.g. yourcompany.com/docs)';
}

function SiteCustomProxyDialog() {
    const { organizationId, site } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();

    const form = useForm<CustomProxyFormInputs>({
        defaultValues: { proxy: site.proxy?.origin ?? '' },
        disabled: isPending,
    });

    const onSubmit: SubmitHandler<CustomProxyFormInputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.sites.updateSiteById(organizationId, site.id, {
                    proxy: data.proxy,
                });
                toast.success('Subdirectory configured');
                close();
            }, 'Error while configuring subdirectory');
        });
    };

    return (
        <Dialog>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <DialogHeader>
                    <Heading>Custom subdirectory</Heading>
                    <Description>
                        Publish your content on a subdirectory of your domain to keep your brand
                        consistent.
                    </Description>
                </DialogHeader>
                <DialogBody>
                    <TextField name="proxy" validate={validateSiteCustomProxy} required>
                        <TextInput autoFocus placeholder="e.g. yourcompany.com/docs" type="text" />
                        <FieldError />
                    </TextField>
                </DialogBody>
                <DialogFooter>
                    <DialogCloseButton />
                    <Submit label="Configure" />
                </DialogFooter>
            </Form>
        </Dialog>
    );
}
